import { useState, useEffect, useCallback } from 'react';
import { serviceAPI, categoryAPI, bookingAPI } from '../services/api';
import toast from 'react-hot-toast';

// ── useServices ──────────────────────────────────────────
export const useServices = (params = {}) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);

  const fetch = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const { data } = await serviceAPI.getAll({ ...params, ...overrides });
      setServices(Array.isArray(data?.data) ? data.data : []);
      setMeta(data?.meta ?? null);
    } catch (err) {
      setServices([]);
      setError(err.response?.data?.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]);

  useEffect(() => { fetch(); }, [fetch]);
  return { services, loading, error, meta, refetch: fetch };
};

// ── useCategories ────────────────────────────────────────
export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    categoryAPI.getAll()
      .then(({ data }) => setCategories(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  return { categories, loading };
};

// ── useService (single) ──────────────────────────────────
export const useService = (slug) => {
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    serviceAPI.getBySlug(slug)
      .then(({ data }) => setService(data.data))
      .catch((err) => setError(err.response?.data?.message || 'Service not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  return { service, loading, error };
};

// ── useBookings ──────────────────────────────────────────
export const useBookings = (params = {}) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  const fetch = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const { data } = await bookingAPI.getAll({ ...params, ...overrides });
      setBookings(Array.isArray(data?.data) ? data.data : []);
      setMeta(data?.meta ?? null);
    } catch {
      setBookings([]);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]);

  useEffect(() => { fetch(); }, [fetch]);
  return { bookings, loading, meta, refetch: fetch };
};

// ── useRazorpay ──────────────────────────────────────────
export const useRazorpay = () => {
  const [loaded, setLoaded] = useState(!!window.Razorpay);

  useEffect(() => {
    if (window.Razorpay) return;
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
  }, []);

  return loaded;
};

// ── useSEO ───────────────────────────────────────────────
export { useSEO } from './useSEO';
